import axios from 'axios'
import store from '../store'
import { getAssetUploadCredential } from '../api/upload'

/** 这次页面里以 trackForDiscard 直传到云、还没被保存请求带上的地址。 */
const pending = new Set()
let savesInFlight = 0

function fileFrom(params) {
  if (typeof File !== 'undefined' && params instanceof File) {
    return params
  }
  if (typeof Blob !== 'undefined' && params instanceof Blob && !(typeof FormData !== 'undefined' && params instanceof FormData)) {
    return params
  }
  if (typeof FormData !== 'undefined' && params instanceof FormData) {
    const file = params.get('file')
    if (file && typeof file === 'object') {
      return file
    }
  }
  return null
}

function discardEndpoint() {
  const base = process.env.NODE_ENV === 'development'
    ? '/api'
    : `${window.location.protocol}//${window.location.host}`
  return `${base}/v1/api/upload/credentials/asset/discard`
}

function remember(url) {
  if (url) {
    pending.add(url)
  }
}

function retainFromText(text) {
  Array.from(pending).forEach((url) => {
    if (text.indexOf(url) >= 0) {
      pending.delete(url)
    }
  })
}

function retainFromPayload(data) {
  if (!data || pending.size === 0) {
    return
  }
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    // 通用资源保存把地址包在 dataJson 这类 JSON 字符串字段里，只能按子串认。
    data.forEach((value) => {
      if (typeof value === 'string') {
        retainFromText(value)
      }
    })
    return
  }
  retainFromText(typeof data === 'string' ? data : JSON.stringify(data))
}

function isBizError(data) {
  let body = data
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body)
    } catch (e) {
      return false
    }
  }
  return !!body && String(body.errCode) === '500'
}

/**
 * 包在 adapter 上计数：request.js 的响应拦截器先注册、只往后传 body，
 * 后注册的响应拦截器拿不到 config；adapter 层在所有拦截器之前，能看到原始响应。
 * 计数也只覆盖真正发出去的请求，发出前被取消的不会留下悬空计数。
 */
function trackSave(adapter) {
  return (config) => {
    savesInFlight += 1
    return new Promise((resolve) => resolve(adapter(config))).then((response) => {
      savesInFlight = Math.max(0, savesInFlight - 1)
      if (!isBizError(response && response.data)) {
        retainFromPayload(config.data)
      }
      return response
    }, (error) => {
      savesInFlight = Math.max(0, savesInFlight - 1)
      throw error
    })
  }
}

// keepalive 请求体合计不能超过 64KB，批量弹窗一次关掉几百行时要分批发
const DISCARD_BATCH_SIZE = 100

function postDiscard(publicUrls) {
  const token = store.getters['user/getToken']
  const batches = []
  for (let start = 0; start < publicUrls.length; start += DISCARD_BATCH_SIZE) {
    batches.push(publicUrls.slice(start, start + DISCARD_BATCH_SIZE))
  }
  return Promise.all(batches.map((batch) => fetch(discardEndpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'user-token': token || '',
    },
    body: JSON.stringify({ publicUrls: batch }),
    keepalive: true,
  }).catch(() => {}))).then(() => {})
}

export function discardUnkeptCloudAssets() {
  if (savesInFlight > 0 || pending.size === 0) {
    return Promise.resolve()
  }
  const publicUrls = Array.from(pending)
  pending.clear()
  return postDiscard(publicUrls)
}

export function discardIssuedUrls(urls) {
  const publicUrls = (urls || []).filter(Boolean)
  if (!publicUrls.length) {
    return Promise.resolve()
  }
  publicUrls.forEach((url) => pending.delete(url))
  return postDiscard(publicUrls)
}

export function releasePending(url) {
  if (!url || !pending.has(url)) {
    return Promise.resolve()
  }
  pending.delete(url)
  return postDiscard([url])
}

export function attachCloudAssetRetention(client) {
  client.interceptors.request.use((config) => {
    const method = (config.method || 'get').toLowerCase()
    const url = config.url || ''
    if (method !== 'get' && url.indexOf('/upload/credentials') < 0) {
      config.adapter = trackSave(config.adapter || axios.defaults.adapter)
    }
    return config
  })
  window.addEventListener('pagehide', () => {
    discardUnkeptCloudAssets()
  })
}

export async function uploadAssetFile(file, options = {}) {
  const credRes = await getAssetUploadCredential({
    fileName: file.name || 'upload.bin',
    contentType: file.type || 'application/octet-stream',
    businessType: 'asset',
  }, { cancelToken: options.cancelToken })
  if (!credRes || !credRes.success || !credRes.data) {
    throw new Error((credRes && (credRes.errMessage || credRes.message)) || '获取上传凭证失败')
  }
  const { uploadUrl, publicUrl, headers } = credRes.data
  const headerCopy = { ...(headers || {}) }
  const signedType = headerCopy['content-type'] || headerCopy['Content-Type']
  delete headerCopy['content-type']
  delete headerCopy['Content-Type']
  await axios.put(uploadUrl, file, {
    headers: {
      ...headerCopy,
      'Content-Type': signedType || file.type || 'application/octet-stream',
    },
    transformRequest: [(data) => data],
    cancelToken: options.cancelToken,
    onUploadProgress: (event) => {
      if (options.onProgress && event.total) {
        options.onProgress(event)
      }
    },
  })
  // 只有表单显式登记的地址才会在离开未保存时删除；上传工具页等只拿链接不保存，删了会断外链。
  if (options.trackForDiscard) {
    remember(publicUrl)
  }
  return { success: true, errCode: '0', data: publicUrl }
}

export function uploadFileDirect(params, options = {}) {
  const file = fileFrom(params)
  if (!file) {
    return null
  }
  return uploadAssetFile(file, options)
}
